import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success(t('footer.subscribeSuccess'));
      setEmail('');
    } catch (error) {
      console.error('Newsletter error:', error);
      toast.error('Error subscribing');
    } finally {
      setIsLoading(false);
    }
  };

  const exploreLinks = [
    { key: 'nav.about', href: '/#about' },
    { key: 'nav.contact', href: '/#contact' },
    { key: 'nav.boutique', href: '/#services' },
    { key: 'nav.services', href: '/#services' },
  ];

  const socialLinks = [
    { name: 'Facebook', href: '#' },
    { name: 'Instagram', href: '#' },
  ];

  return (
    <footer 
      className="py-20 md:py-24 bg-white border-t border-neutral-100"
      data-testid="main-footer"
    >
      <div className="container-custom">
        {/* Large Brand Name */}
        <motion.div 
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          data-testid="footer-brand"
        >
          <h2 className="font-serif font-bold text-5xl md:text-7xl lg:text-8xl tracking-tight text-black">
            BAO AGENCY
          </h2>
        </motion.div>

        {/* Links Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 mb-16">
          {/* Explore */}
          <div data-testid="footer-explore">
            <h3 className="font-sans font-semibold text-sm uppercase tracking-wider mb-6 text-black">
              {t('footer.explore')}
            </h3>
            <nav className="flex flex-col gap-3">
              {exploreLinks.map((link) => (
                <Link
                  key={link.key}
                  to={link.href}
                  className="text-neutral-600 hover:text-black transition-colors font-sans"
                  data-testid={`footer-link-${link.key.split('.')[1]}`}
                >
                  {t(link.key)}
                </Link>
              ))}
            </nav>
          </div>

          {/* Follow Us */}
          <div data-testid="footer-social">
            <h3 className="font-sans font-semibold text-sm uppercase tracking-wider mb-6 text-black">
              {t('footer.follow')}
            </h3>
            <nav className="flex flex-col gap-3">
              {socialLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-neutral-600 hover:text-black transition-colors font-sans"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </a>
              ))}
            </nav>
          </div>

          {/* Newsletter */}
          <div data-testid="footer-newsletter">
            <p className="text-neutral-600 font-sans mb-6 max-w-sm">
              {t('footer.newsletter')}
            </p>
            <form 
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row gap-4"
              data-testid="newsletter-form"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.emailPlaceholder')}
                required
                className="flex-1 px-4 py-3 rounded-full border border-neutral-200 focus:border-black focus:ring-1 focus:ring-black outline-none transition-all"
                data-testid="newsletter-email-input"
              />
              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="btn-pill btn-primary whitespace-nowrap disabled:opacity-50"
                data-testid="newsletter-submit-button"
              >
                {isLoading ? '...' : t('footer.subscribe')}
              </motion.button>
            </form>
          </div>
        </div>

        {/* Copyright */}
        <div 
          className="pt-8 border-t border-neutral-100 text-center"
          data-testid="footer-copyright"
        >
          <p className="text-neutral-500 text-sm font-sans">
            © {new Date().getFullYear()} BAO Agency. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
